import { apiUrl } from './apiBase';
import { buildJarvisSystemPrompt }  from '../prompts/jarvisSystem';
import { buildExtractionPrompt }    from '../prompts/extractionPrompt';
import { buildAnalysisPrompt, buildPineScriptPrompt, buildScriptExtractionPrompt, buildTranscriptExtractionPrompt } from '../prompts/analysisPrompt';
import { buildDailyRecapPrompt }    from '../prompts/dailyPrompt';
import { buildWeeklyBriefingPrompt } from '../prompts/weeklyPrompt';

import {
  getFullJarvisContext,
  saveRule,
  saveKnowledge,
  saveKnowledgeChunk,
  searchKnowledgeChunks,
  saveBehavior,
  savePattern,
  saveDailyLesson,
  savePrediction,
  incrementDiscussions,
  updateJarvisMemory,
  getRules,
} from './storage';

// ─── Core API caller ─────────────────────────────────────────────────
const callClaude = async (messages, systemPrompt, maxTokens = 2000) => {
  const res = await fetch(apiUrl('/api/chat'), {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      max_tokens: maxTokens,
      system:     systemPrompt,
      messages,
    }),
  });

  const data = await res.json();

  // Friendly rate-limit message instead of cryptic error
  if (res.status === 429 || data.error === 'RATE_LIMITED') {
    const wait = data.retryAfter || 60;
    throw new Error(`⏳ JARVIS AI is cooling down. Wait ${wait} seconds and try again. (Free tier limit)`);
  }

  if (!res.ok || data.error) {
    const msg = data.error?.message || data.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }

  return data.content[0].text;
};

// ─── Sanitise JSON returned by Claude ────────────────────────────────
// Claude sometimes wraps JSON in ```json ... ``` fences — strip them.
const parseJSON = (raw) => {
  const clean = raw
    .trim()
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/,      '')
    .replace(/```\s*$/,      '');
  return JSON.parse(clean);
};

// ─── Main JARVIS chat ─────────────────────────────────────────────────
export const askJarvis = async (
  userMessage,
  conversationHistory = [],
  screenshot = null,
) => {
  const context        = getFullJarvisContext();
  const relevantChunks = searchKnowledgeChunks(userMessage, 2);
  const systemPrompt   = buildJarvisSystemPrompt(context, relevantChunks);

  // Build user message content — text + optional image
  const content = [];
  if (screenshot) {
    content.push({
      type:   'image',
      source: { type: 'base64', media_type: 'image/jpeg', data: screenshot },
    });
  }
  content.push({ type: 'text', text: userMessage });

  // Keep last 4 turns for Groq token budget (older context is in system prompt)
  const history = conversationHistory.slice(-4);

  const reply = await callClaude(
    [...history, { role: 'user', content }],
    systemPrompt,
    2500,
  );

  // ── Side effects (non-blocking, failures are silent) ──────────────
  incrementDiscussions();
  if (screenshot) {
    const m = context.jarvisMemory;
    updateJarvisMemory({ total_screenshots: (m.total_screenshots || 0) + 1 });
  }

  // Auto-extract rules / behaviors / patterns in background
  autoExtract(userMessage, reply).catch(() => {});

  // Log prediction if JARVIS gave a trade direction
  logPredictionIfPresent(reply, context.currentPrice);

  return reply;
};

// ─── Screenshot / chart analysis ─────────────────────────────────────
export const analyzeScreenshot = async (base64Image, userNote = '') => {
  const prompt = buildAnalysisPrompt(userNote);
  return askJarvis(prompt, [], base64Image);
};

// ─── Pine Script reader (chat-friendly text output) ──────────────────
export const analyzePineScript = async (scriptCode) => {
  const context = getFullJarvisContext();
  const prompt  = buildPineScriptPrompt(scriptCode);
  return callClaude(
    [{ role: 'user', content: prompt }],
    buildJarvisSystemPrompt(context),
    2000,
  );
};

// ─── Extract rules from script + auto-save to Rule Book ──────────────
// Called when Sir clicks "JARVIS Analyze & Save Rules" in the Scripts tab.
// Returns { rulesCount, summary, rules, filters, confluence_tip } on success.
export const extractAndSaveScript = async (scriptCode, scriptTitle = '') => {
  const prompt = buildScriptExtractionPrompt(scriptCode, scriptTitle);
  const raw    = await callClaude(
    [{ role: 'user', content: prompt }],
    'You are JARVIS — extract trading rules from Pine Script. Return ONLY valid JSON. No markdown.',
    2500,
  );

  const data = parseJSON(raw);

  // Save each rule to Rule Book (with dedup via seed-like title check)
  const existingRules = getRules();
  let savedCount = 0;
  if (Array.isArray(data.rules)) {
    for (const rule of data.rules) {
      const alreadyExists = existingRules.some(r =>
        r.title?.toLowerCase() === rule.title?.toLowerCase() &&
        r.source?.includes(scriptTitle)
      );
      if (!alreadyExists) {
        saveRule({
          ...rule,
          source:         `Pine Script: ${scriptTitle || 'Unnamed'}`,
          auto_extracted: true,
        });
        savedCount++;
      }
    }
  }

  // Save script itself to knowledge base
  saveKnowledge({
    title:       scriptTitle || 'Pine Script',
    type:        'script',
    source:      'TradingView Pine Script',
    raw_content: scriptCode,
    category:    'Script',
    description: data.summary || '',
  });

  return {
    rulesCount:     savedCount,
    totalExtracted: (data.rules || []).length,
    summary:        data.summary || '',
    core_concept:   data.core_concept || '',
    rules:          data.rules || [],
    key_behaviors:  data.key_behaviors || [],
    filters:        data.filters || [],
    confluence_tip: data.confluence_tip || '',
  };
};

// ─── Extract structured knowledge from a transcript / YouTube notes ──────────
// Sir pastes transcript text → AI extracts 5 categories → saved as searchable chunks.
// Returns { savedCount, source_summary, categories }
export const extractTranscript = async (transcriptText, sourceName = '') => {
  const prompt = buildTranscriptExtractionPrompt(transcriptText, sourceName);
  const raw    = await callClaude(
    [{ role: 'user', content: prompt }],
    'You are JARVIS — extract trading knowledge from transcripts. Return ONLY valid JSON.',
    3000,
  );

  const data = parseJSON(raw);

  const categoryMap = {
    key_concepts: 'concept',
    rules:        'rule',
    examples:     'example',
    mistakes:     'mistake',
    checklist:    'checklist',
  };

  let savedCount = 0;
  const categorySummary = {};
  for (const [key, catLabel] of Object.entries(categoryMap)) {
    const items = data[key] || [];
    categorySummary[catLabel] = items.length;
    for (const item of items) {
      saveKnowledgeChunk({
        source:   sourceName,
        category: catLabel,
        title:    item.title   || '',
        content:  item.content || '',
        tags:     item.tags    || [],
      });
      savedCount++;
    }
  }

  // Also save the source itself to the knowledge base (for backward compat)
  saveKnowledge({
    title:       sourceName || 'Transcript',
    type:        'transcript',
    source:      sourceName || 'YouTube / Manual',
    raw_content: transcriptText.slice(0, 1500),
    description: data.source_summary || '',
  });

  return {
    savedCount,
    source_summary: data.source_summary || '',
    categories: categorySummary,
    key_concepts: data.key_concepts || [],
    rules:        data.rules        || [],
    examples:     data.examples     || [],
    mistakes:     data.mistakes     || [],
    checklist:    data.checklist    || [],
  };
};

// ─── Daily recap processor ────────────────────────────────────────────
// Called when Sir submits the evening Daily Recap form.
// Returns parsed JSON analysis from JARVIS.
export const processDailyRecap = async (recap) => {
  const context = getFullJarvisContext();
  const prompt  = buildDailyRecapPrompt(recap, context);

  const raw = await callClaude(
    [{ role: 'user', content: prompt }],
    'You are JARVIS processing a daily trading lesson. Return only valid JSON — no markdown, no explanation.',
    1200,
  );

  const analysis = parseJSON(raw);

  // Persist extracted data
  if (analysis.behaviors_new?.length) {
    analysis.behaviors_new.forEach(b => saveBehavior({ ...b, learned_from: 'Daily recap' }));
  }
  if (analysis.patterns_new?.length) {
    analysis.patterns_new.forEach(p => savePattern({ ...p, auto_discovered: true }));
  }
  if (analysis.rules_new?.length) {
    analysis.rules_new.forEach(r => saveRule({ ...r, source: 'Daily recap', auto_extracted: true }));
  }

  // Save JARVIS summary back onto the lesson record
  saveDailyLesson({
    ...recap,
    jarvis_summary:      analysis.summary,
    jarvis_takeaway:     analysis.key_takeaway,
    behaviors_extracted: analysis.behaviors_new || [],
    patterns_extracted:  analysis.patterns_new  || [],
    tomorrow_watch:      analysis.tomorrow_watch,
  });

  return analysis;
};

// ─── Weekly self-briefing ─────────────────────────────────────────────
export const generateWeeklyBriefing = async (weekData) => {
  const context = getFullJarvisContext();
  const prompt  = buildWeeklyBriefingPrompt({
    ...weekData,
    rules:        context.rules,
    behaviors:    context.behaviors,
    patterns:     context.patterns,
    jarvisMemory: context.jarvisMemory,
  });

  const raw = await callClaude(
    [{ role: 'user', content: prompt }],
    'You are JARVIS generating your weekly self-analysis. Return only valid JSON.',
    1800,
  );

  const briefing = parseJSON(raw);

  // Save briefing to JARVIS memory
  const memory = context.jarvisMemory;
  const briefings = memory.weekly_briefings || [];
  updateJarvisMemory({
    weekly_briefings: [...briefings, { ...briefing, generated_at: new Date().toISOString() }],
    self_weaknesses:  briefing.self_weakness_identified
      ? [briefing.self_weakness_identified]
      : memory.self_weaknesses,
    self_strengths:   briefing.self_strength_identified
      ? [briefing.self_strength_identified]
      : memory.self_strengths,
  });

  return briefing;
};

// ─── Auto-extraction (runs silently after every chat message) ─────────
const autoExtract = async (userMessage, jarvisReply) => {
  const prompt = buildExtractionPrompt(userMessage, jarvisReply);

  const raw = await callClaude(
    [{ role: 'user', content: prompt }],
    'You are a JSON extractor. Return only valid JSON — no markdown fences, no explanation.',
    700,
  );

  let data;
  try {
    data = parseJSON(raw);
  } catch {
    return; // Malformed JSON — skip silently
  }

  if (data.hasNewRule && data.rule?.title && data.rule?.action) {
    saveRule({ ...data.rule, auto_extracted: true });
  }
  if (data.hasNewBehavior && data.behavior?.title) {
    saveBehavior({ ...data.behavior, learned_from: 'Chat auto-extraction' });
  }
  if (data.hasNewPattern && data.pattern?.title) {
    savePattern({ ...data.pattern, auto_discovered: true });
  }
  if (data.hasDailyLesson && data.dailyLesson?.market_behavior) {
    saveDailyLesson(data.dailyLesson);
  }
};

// ─── Prediction logger ────────────────────────────────────────────────
// Scans JARVIS reply for BUY/SELL signal + grade, then logs it.
const logPredictionIfPresent = (reply, currentPrice) => {
  // Only log if JARVIS gave a graded trade setup
  const gradeMatch  = reply.match(/GRADE\s*[:：]\s*([A-D+]+)/i);
  const dirMatch    = reply.match(/RECOMMENDATION\s*[:：][^\n]*\b(BUY|SELL)\b/i);
  const confMatch   = reply.match(/JARVIS\s+CONFIDENCE\s*[:：]\s*(\d+)%/i);

  if (!gradeMatch || !dirMatch) return;

  const grade      = gradeMatch[1].trim().toUpperCase();
  const direction  = dirMatch[1].trim().toUpperCase();
  const confidence = confMatch ? parseInt(confMatch[1], 10) : 0;

  // Only log A or A+ grade predictions (B/C/D are "skip" advice — not worth tracking)
  if (!['A+', 'A'].includes(grade)) return;

  savePrediction({
    direction,
    grade,
    confidence_given:    confidence,
    price_at_prediction: currentPrice,
    reply_summary:       reply.substring(0, 400),
  });
};
